<template>
  <div
    ref="rootRef"
    class="hardware-callout-scroller"
    :class="{
      'hardware-callout-scroller--static': isStaticMode,
      'hardware-callout-scroller--compact': isCompactViewport,
      'hardware-callout-scroller--viewport-fit': viewportFit
    }"
    :style="rootStyle"
    :aria-label="ariaLabel"
    data-text-reveal="off"
  >
    <div ref="stageRef" class="hardware-callout-scroller__stage">
      <img
        class="hardware-callout-scroller__image"
        :src="displayedImageSrc"
        :alt="imageAlt"
        :loading="loading"
        :style="imageStyle"
      />

      <button
        v-if="hasImageFrameSequence && !isMobileFramePlaying"
        type="button"
        class="hardware-callout-scroller__mobile-play"
        :aria-label="mobileReplayLabel"
        :title="mobileReplayLabel"
        @click="playMobileFrameSequence"
      >
        <span class="hardware-callout-scroller__mobile-play-icon" aria-hidden="true"></span>
      </button>

      <img
        v-if="scrollHintSrc && !isViewportScrollHint"
        class="hardware-callout-scroller__scroll-hint"
        :src="scrollHintSrc"
        :alt="scrollHintAlt"
        aria-hidden="true"
      />

      <svg
        v-if="normalizedItems.length"
        class="hardware-callout-scroller__connectors"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <line
          v-for="(item, index) in normalizedItems"
          :key="`${item.key}-line`"
          class="hardware-callout-scroller__connector"
          :x1="getLinePoint(item, index, 'from').x"
          :y1="getLinePoint(item, index, 'from').y"
          :x2="getLinePoint(item, index, 'to').x"
          :y2="getLinePoint(item, index, 'to').y"
          pathLength="1"
          vector-effect="non-scaling-stroke"
          :data-callout-line="index"
          :data-callout-part="index"
          :style="getItemColorStyle(item)"
        />
      </svg>

      <span
        v-for="(item, index) in normalizedItems"
        :key="`${item.key}-dot`"
        class="hardware-callout-scroller__dot"
        :style="[getDotStyle(item), getItemColorStyle(item)]"
        :data-callout-dot="index"
        :data-callout-part="index"
        aria-hidden="true"
      ></span>

      <article
        v-for="(item, index) in normalizedItems"
        :key="`${item.key}-note`"
        class="hardware-callout-scroller__note"
        :class="`hardware-callout-scroller__note--${item.side}`"
        :style="[getNoteStyle(item), getItemColorStyle(item)]"
        :data-callout-note="index"
        :data-callout-part="index"
      >
        <h3>
          <span
            class="hardware-callout-scroller__note-title-text"
            :data-callout-title="index"
          >{{ item.title }}</span>
        </h3>
        <p v-for="(line, lineIndex) in item.lines" :key="lineIndex">
          <template v-for="(part, partIndex) in line" :key="partIndex">
            <span
              :class="{ 'hardware-callout-scroller__note-highlight': part.highlight }"
            >{{ part.text }}</span>
          </template>
        </p>
      </article>
    </div>

    <img
      v-if="scrollHintSrc && isViewportScrollHint"
      class="hardware-callout-scroller__scroll-hint hardware-callout-scroller__scroll-hint--viewport"
      :src="scrollHintSrc"
      :alt="scrollHintAlt"
      aria-hidden="true"
    />

    <div class="hardware-callout-scroller__mobile-list">
      <article
        v-for="item in normalizedItems"
        :key="`${item.key}-mobile`"
        class="hardware-callout-scroller__mobile-card"
        :style="getItemColorStyle(item)"
      >
        <h3>{{ item.title }}</h3>
        <p v-for="(line, lineIndex) in item.lines" :key="lineIndex">
          <template v-for="(part, partIndex) in line" :key="partIndex">
            <span
              :class="{ 'hardware-callout-scroller__note-highlight': part.highlight }"
            >{{ part.text }}</span>
          </template>
        </p>
      </article>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
  imageSrc: {
    type: String,
    required: true
  },
  imageFrames: {
    type: Array,
    default: () => []
  },
  imageAlt: {
    type: String,
    default: ''
  },
  ariaLabel: {
    type: String,
    default: ''
  },
  items: {
    type: Array,
    default: () => []
  },
  imageLayout: {
    type: Object,
    default: () => ({})
  },
  accentColor: {
    type: String,
    default: 'var(--pixso-color-primary)'
  },
  stageAspectRatio: {
    type: String,
    default: '1627 / 799'
  },
  stageRatio: {
    type: Number,
    default: 0
  },
  viewportFit: {
    type: Boolean,
    default: false
  },
  viewportHeight: {
    type: String,
    default: 'calc(100vh - var(--pixso-nav-height) - 32px)'
  },
  viewportInset: {
    type: String,
    default: 'var(--pixso-page-padding)'
  },
  scrollStep: {
    type: Number,
    default: 420
  },
  frameScrollStep: {
    type: Number,
    default: 900
  },
  pin: {
    type: Boolean,
    default: true
  },
  pinOffset: {
    type: Number,
    default: 24
  },
  scrub: {
    type: [Number, Boolean],
    default: 0.8
  },
  desktopMinWidth: {
    type: Number,
    default: 1201
  },
  desktopMinHeight: {
    type: Number,
    default: 0
  },
  mobileImageWidth: {
    type: String,
    default: 'min(100%, 420px)'
  },
  loading: {
    type: String,
    default: 'lazy'
  },
  mobileReplayLabel: {
    type: String,
    default: 'Play animation'
  },
  scrollHintSrc: {
    type: String,
    default: ''
  },
  scrollHintAlt: {
    type: String,
    default: ''
  },
  scrollHintPlacement: {
    type: String,
    default: 'stage',
    validator: (value) => ['stage', 'viewport'].includes(value)
  },
  scrollHintBottom: {
    type: String,
    default: '6vh'
  },
  scrollHintViewportStart: {
    type: String,
    default: 'top 88%'
  }
})

const rootRef = ref(null)
const stageRef = ref(null)
const isStaticMode = ref(false)
const isCompactViewport = ref(false)
const isMobileFramePlaying = ref(false)
const connectorAnchorPoints = ref({})
const frameState = reactive({ current: 0 })
let motionMedia = null
let mobileFrameTween = null
let connectorResizeObserver = null
let connectorSyncFrame = 0
let connectorSyncFollowupFrame = 0

const formatCssValue = (value, fallback) => {
  if (value === undefined || value === null || value === '') {
    return fallback
  }

  return typeof value === 'number' ? `${value}%` : String(value)
}

const toNumber = (value, fallback = 0) => {
  if (value === undefined || value === null || value === '') {
    return fallback
  }

  const next = Number.parseFloat(value)
  return Number.isFinite(next) ? next : fallback
}

const normalizePoint = (point, fallback = { x: 0, y: 0 }) => ({
  x: toNumber(point?.x, fallback.x),
  y: toNumber(point?.y, fallback.y)
})

const normalizeLineEndpoint = (endpoint, fallback = { x: 0, y: 0 }) => {
  const point = normalizePoint(endpoint, fallback)

  return {
    ...point,
    anchor: typeof endpoint?.anchor === 'string' ? endpoint.anchor : '',
    gap: toNumber(endpoint?.gap, 14),
    vertical: toNumber(endpoint?.vertical, 0.52),
    offsetX: toNumber(endpoint?.offsetX, 0),
    offsetY: toNumber(endpoint?.offsetY, 0)
  }
}

const normalizeLineParts = (line) => {
  if (Array.isArray(line)) {
    return line.map((part) => (
      typeof part === 'string'
        ? { text: part, highlight: false }
        : { text: part?.text ?? '', highlight: Boolean(part?.highlight) }
    ))
  }

  return [{ text: String(line ?? ''), highlight: false }]
}

const normalizedItems = computed(() => props.items.map((item, index) => {
  const dot = normalizePoint(item.dot || item.point || item.target)
  const line = item.line || {}
  const lineFrom = normalizeLineEndpoint(line.from || item.connector || item.anchor || dot, dot)
  const lineTo = normalizeLineEndpoint(line.to || dot, dot)
  const noteWidth = item.note?.width ?? 28
  const noteX = item.note?.x ?? (item.note?.right !== undefined ? undefined : 0)
  const noteY = item.note?.y ?? 0
  const noteRight = item.note?.right
  const side = item.side || item.note?.side || (toNumber(noteX, 0) > 50 || noteRight !== undefined ? 'right' : 'left')
  const rawLines = item.lines || item.description || []
  const lines = Array.isArray(rawLines)
    ? rawLines.map(normalizeLineParts)
    : [normalizeLineParts(rawLines)]

  return {
    key: item.key || `callout-${index}`,
    title: item.title || '',
    color: item.color || props.accentColor,
    side,
    lines,
    dot,
    line: {
      from: lineFrom,
      to: lineTo
    },
    note: {
      x: noteX,
      y: noteY,
      right: noteRight,
      width: noteWidth,
      align: item.note?.align || 'left'
    }
  }
}))

const parsedStageRatio = computed(() => {
  if (props.stageRatio > 0) {
    return props.stageRatio
  }

  const [width, height] = String(props.stageAspectRatio)
    .split('/')
    .map((value) => Number.parseFloat(value))

  if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
    return width / height
  }

  return 1627 / 799
})

const normalizedImageFrames = computed(() => (
  Array.isArray(props.imageFrames)
    ? props.imageFrames.filter(Boolean)
    : []
))

const hasImageFrameSequence = computed(() => normalizedImageFrames.value.length > 1)
const isViewportScrollHint = computed(() => props.scrollHintPlacement === 'viewport')

const displayedImageSrc = computed(() => {
  const frames = normalizedImageFrames.value

  if (!frames.length) {
    return props.imageSrc
  }

  const frameIndex = Math.min(
    frames.length - 1,
    Math.max(0, Math.round(frameState.current))
  )

  return frames[frameIndex]
})

const motionSignature = computed(() => JSON.stringify({
  keys: normalizedItems.value.map((item) => item.key),
  frameCount: normalizedImageFrames.value.length,
  pin: props.pin,
  desktopMinWidth: props.desktopMinWidth,
  desktopMinHeight: props.desktopMinHeight,
  scrollStep: props.scrollStep,
  frameScrollStep: props.frameScrollStep,
  stageAspectRatio: props.stageAspectRatio,
  stageRatio: props.stageRatio,
  viewportFit: props.viewportFit,
  viewportHeight: props.viewportHeight,
  viewportInset: props.viewportInset,
  scrollHintPlacement: props.scrollHintPlacement,
  scrollHintBottom: props.scrollHintBottom,
  scrollHintViewportStart: props.scrollHintViewportStart
}))

const connectorLayoutSignature = computed(() => JSON.stringify(normalizedItems.value.map((item) => ({
  key: item.key,
  title: item.title,
  side: item.side,
  dot: item.dot,
  line: item.line,
  note: item.note,
  lines: item.lines
}))))

const rootStyle = computed(() => ({
  '--hardware-callout-accent': props.accentColor,
  '--hardware-callout-aspect-ratio': props.stageAspectRatio,
  '--hardware-callout-stage-ratio': parsedStageRatio.value,
  '--hardware-callout-viewport-height': props.viewportHeight,
  '--hardware-callout-viewport-inset': props.viewportInset,
  '--hardware-callout-mobile-image-width': props.mobileImageWidth,
  '--hardware-callout-scroll-hint-bottom': props.scrollHintBottom
}))

const imageStyle = computed(() => {
  const layout = props.imageLayout || {}
  const style = {
    left: formatCssValue(layout.left, '0%'),
    top: formatCssValue(layout.top, '0%'),
    width: formatCssValue(layout.width, '100%'),
    objectFit: layout.objectFit || 'contain',
    objectPosition: layout.objectPosition || 'center'
  }

  if (layout.height !== undefined) {
    style.height = formatCssValue(layout.height, 'auto')
  }

  if (layout.transform) {
    style.transform = layout.transform
  }

  return style
})

const getItemColorStyle = (item) => ({
  '--hardware-callout-item-color': item.color
})

const getLinePoint = (item, index, edge) => (
  connectorAnchorPoints.value[`${index}:${edge}`] || item.line[edge]
)

const getDotStyle = (item) => ({
  left: formatCssValue(item.dot.x, '0%'),
  top: formatCssValue(item.dot.y, '0%')
})

const getNoteStyle = (item) => {
  const style = {
    top: formatCssValue(item.note.y, '0%'),
    width: formatCssValue(item.note.width, '28%'),
    textAlign: item.note.align
  }

  if (item.note.right !== undefined) {
    style.right = formatCssValue(item.note.right, '0%')
  } else {
    style.left = formatCssValue(item.note.x, '0%')
  }

  return style
}

const getNavOffset = () => {
  if (typeof window === 'undefined') {
    return 72 + props.pinOffset
  }

  const navHeight = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--pixso-nav-height')
  ) || 72

  return navHeight + props.pinOffset
}

const resetMotion = () => {
  motionMedia?.revert()
  motionMedia = null
}

const stopMobileFrameTween = () => {
  mobileFrameTween?.kill()
  mobileFrameTween = null
  isMobileFramePlaying.value = false
}

const playMobileFrameSequence = () => {
  if (!hasImageFrameSequence.value) {
    return
  }

  stopMobileFrameTween()

  const lastFrame = normalizedImageFrames.value.length - 1
  const reducedMotion = typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  frameState.current = reducedMotion ? lastFrame : 0

  if (reducedMotion) {
    return
  }

  isMobileFramePlaying.value = true
  mobileFrameTween = gsap.to(frameState, {
    current: lastFrame,
    duration: Math.min(3.2, Math.max(1.35, normalizedImageFrames.value.length / 24)),
    ease: 'none',
    snap: { current: 1 },
    onComplete: () => {
      mobileFrameTween = null
      isMobileFramePlaying.value = false
    }
  })
}

const getTitleAnchorPoint = (titleEl, endpoint) => {
  const noteEl = titleEl.closest('[data-callout-note]')

  if (!noteEl || !stageRef.value) {
    return null
  }

  const stageWidth = stageRef.value.offsetWidth
  const stageHeight = stageRef.value.offsetHeight

  if (!stageWidth || !stageHeight) {
    return null
  }

  const titleLeft = noteEl.offsetLeft + titleEl.offsetLeft
  const titleTop = noteEl.offsetTop + titleEl.offsetTop
  const anchor = endpoint.anchor
  let x = titleLeft

  if (anchor.includes('end') || anchor.includes('right')) {
    x += titleEl.offsetWidth + endpoint.gap
  } else if (anchor.includes('center')) {
    x += titleEl.offsetWidth / 2
  } else {
    x -= endpoint.gap
  }

  const y = titleTop + titleEl.offsetHeight * endpoint.vertical

  return {
    x: ((x + endpoint.offsetX) / stageWidth) * 100,
    y: ((y + endpoint.offsetY) / stageHeight) * 100
  }
}

const syncConnectorAnchors = () => {
  if (!rootRef.value || !stageRef.value) {
    connectorAnchorPoints.value = {}
    return
  }

  const next = {}

  normalizedItems.value.forEach((item, index) => {
    const titleEl = rootRef.value.querySelector(`[data-callout-title="${index}"]`)

    if (!titleEl) {
      return
    }

    ;['from', 'to'].forEach((edge) => {
      const endpoint = item.line[edge]

      if (!endpoint?.anchor) {
        return
      }

      const point = getTitleAnchorPoint(titleEl, endpoint)

      if (point) {
        next[`${index}:${edge}`] = point
      }
    })
  })

  connectorAnchorPoints.value = next
}

const scheduleConnectorSync = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.cancelAnimationFrame(connectorSyncFrame)
  window.cancelAnimationFrame(connectorSyncFollowupFrame)
  connectorSyncFrame = window.requestAnimationFrame(() => {
    syncConnectorAnchors()
    connectorSyncFollowupFrame = window.requestAnimationFrame(() => {
      syncConnectorAnchors()
      ScrollTrigger.refresh()
    })
  })
}

const setupConnectorObserver = () => {
  if (typeof window === 'undefined' || !stageRef.value) {
    return
  }

  connectorResizeObserver?.disconnect()

  if ('ResizeObserver' in window) {
    connectorResizeObserver = new window.ResizeObserver(scheduleConnectorSync)
    connectorResizeObserver.observe(stageRef.value)
    rootRef.value
      ?.querySelectorAll('[data-callout-note], [data-callout-title]')
      .forEach((element) => connectorResizeObserver.observe(element))
  }

  window.addEventListener('resize', scheduleConnectorSync)
  document.fonts?.ready?.then(scheduleConnectorSync)
}

const resetConnectorObserver = () => {
  if (typeof window !== 'undefined') {
    window.cancelAnimationFrame(connectorSyncFrame)
    window.cancelAnimationFrame(connectorSyncFollowupFrame)
    window.removeEventListener('resize', scheduleConnectorSync)
  }

  connectorResizeObserver?.disconnect()
  connectorResizeObserver = null
}

const syncCompactViewport = () => {
  if (typeof window === 'undefined') {
    isCompactViewport.value = false
    return
  }

  const hasHeightGuard = Number.isFinite(props.desktopMinHeight) && props.desktopMinHeight > 0
  isCompactViewport.value = window.innerWidth < props.desktopMinWidth ||
    (hasHeightGuard && window.innerHeight < props.desktopMinHeight)
}

const getDesktopMediaQuery = () => {
  const conditions = [
    `(min-width: ${props.desktopMinWidth}px)`
  ]

  if (Number.isFinite(props.desktopMinHeight) && props.desktopMinHeight > 0) {
    conditions.push(`(min-height: ${props.desktopMinHeight}px)`)
  }

  conditions.push('(prefers-reduced-motion: no-preference)')
  return conditions.join(' and ')
}

const getStaticMediaQuery = () => {
  const conditions = [
    `(max-width: ${props.desktopMinWidth - 1}px)`,
    '(prefers-reduced-motion: reduce)'
  ]

  if (Number.isFinite(props.desktopMinHeight) && props.desktopMinHeight > 0) {
    conditions.splice(1, 0, `(max-height: ${props.desktopMinHeight - 1}px)`)
  }

  return conditions.join(', ')
}

const setupMotion = () => {
  resetMotion()
  stopMobileFrameTween()
  frameState.current = normalizedImageFrames.value.length
    ? normalizedImageFrames.value.length - 1
    : 0

  if (!props.pin || !rootRef.value || !stageRef.value || normalizedItems.value.length === 0) {
    isStaticMode.value = true
    return
  }

  motionMedia = gsap.matchMedia()

  motionMedia.add(
    getDesktopMediaQuery(),
    () => {
      isStaticMode.value = false

      const root = rootRef.value
      const stage = stageRef.value
      const lines = Array.from(root.querySelectorAll('[data-callout-line]'))
      const dots = Array.from(root.querySelectorAll('[data-callout-dot]'))
      const notes = Array.from(root.querySelectorAll('[data-callout-note]'))
      const scrollHint = root.querySelector('.hardware-callout-scroller__scroll-hint')
      const frameCount = normalizedImageFrames.value.length
      const hasFrameSequence = frameCount > 1
      const hasViewportScrollHint = isViewportScrollHint.value
      let earlyScrollHintTrigger = null

      if (scrollHint) {
        gsap.set(scrollHint, {
          autoAlpha: hasViewportScrollHint ? 0 : 1
        })
      }
      gsap.set(lines, {
        autoAlpha: 0
      })
      gsap.set(dots, {
        autoAlpha: 0,
        '--hardware-callout-dot-scale': 0.4
      })
      gsap.set(notes, {
        autoAlpha: 0,
        y: 24
      })
      frameState.current = hasFrameSequence ? 0 : 0

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: root,
          start: () => `top top+=${getNavOffset()}`,
          end: () => `+=${Math.max(1, normalizedItems.value.length + 1) * props.scrollStep + (hasFrameSequence ? props.frameScrollStep : 0)}`,
          pin: stage,
          pinSpacing: true,
          scrub: props.scrub,
          anticipatePin: 1,
          invalidateOnRefresh: true
        }
      })

      let cursor = 0.35

      if (scrollHint && hasViewportScrollHint) {
        const isBeforeMainScrollStart = () => {
          const currentY = window.scrollY || window.pageYOffset || 0
          return !timeline.scrollTrigger || currentY < timeline.scrollTrigger.start
        }

        earlyScrollHintTrigger = ScrollTrigger.create({
          trigger: root,
          start: props.scrollHintViewportStart,
          endTrigger: root,
          end: () => `top top+=${getNavOffset()}`,
          onEnter: () => {
            if (isBeforeMainScrollStart()) {
              gsap.set(scrollHint, { autoAlpha: 1 })
            }
          },
          onEnterBack: () => {
            if (isBeforeMainScrollStart()) {
              gsap.set(scrollHint, { autoAlpha: 1 })
            }
          },
          onLeaveBack: () => gsap.set(scrollHint, { autoAlpha: 0 }),
          invalidateOnRefresh: true
        })
      }

      if (scrollHint) {
        timeline.to(scrollHint, {
          autoAlpha: 1,
          duration: 0.12,
          ease: 'power2.out'
        }, 0.02)
      }

      if (hasFrameSequence) {
        timeline.to(frameState, {
          current: frameCount - 1,
          duration: Math.max(0.8, props.frameScrollStep / props.scrollStep),
          ease: 'none',
          snap: { current: 1 }
        }, 0)

        cursor += Math.max(0.8, props.frameScrollStep / props.scrollStep)
      }

      normalizedItems.value.forEach((item, index) => {
        const line = lines[index]
        const dot = dots[index]
        const note = notes[index]

        timeline
          .to(line, {
            autoAlpha: 1,
            duration: 0.38,
            ease: 'power2.out'
          }, cursor)
          .to(dot, {
            autoAlpha: 1,
            '--hardware-callout-dot-scale': 1,
            duration: 0.24,
            ease: 'power2.out'
          }, cursor + 0.16)
          .to(note, {
            autoAlpha: 1,
            y: 0,
            duration: 0.42,
            ease: 'power3.out'
          }, cursor + 0.2)

        cursor += 0.85
      })

      if (scrollHint) {
        timeline.to(scrollHint, {
          autoAlpha: 0,
          duration: hasViewportScrollHint ? 0.12 : 0.28,
          ease: 'power2.out'
        }, cursor + 0.12)
      }

      timeline.to({}, { duration: 0.45 })
      ScrollTrigger.refresh()

      return () => {
        earlyScrollHintTrigger?.kill()
        timeline.kill()
        gsap.set([...lines, ...dots, ...notes, scrollHint].filter(Boolean), { clearProps: 'all' })
        frameState.current = normalizedImageFrames.value.length
          ? normalizedImageFrames.value.length - 1
          : 0
      }
    }
  )

  motionMedia.add(
    getStaticMediaQuery(),
    () => {
      isStaticMode.value = true
      frameState.current = normalizedImageFrames.value.length
        ? normalizedImageFrames.value.length - 1
        : 0
      gsap.set(rootRef.value?.querySelectorAll('[data-callout-part]') || [], { clearProps: 'all' })

      return () => {
        isStaticMode.value = false
      }
    }
  )
}

onMounted(async () => {
  await nextTick()
  syncCompactViewport()
  preloadImageFrames()

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', syncCompactViewport)
  }

  setupMotion()
  setupConnectorObserver()
  scheduleConnectorSync()
})

watch(
  motionSignature,
  async () => {
    await nextTick()
    preloadImageFrames()
    syncCompactViewport()
    setupMotion()
    setupConnectorObserver()
    scheduleConnectorSync()
  },
  { flush: 'post' }
)

const preloadImageFrames = () => {
  if (typeof window === 'undefined') {
    return
  }

  normalizedImageFrames.value.forEach((src) => {
    const image = new window.Image()
    image.src = src
  })
}

watch(
  connectorLayoutSignature,
  async () => {
    await nextTick()
    setupConnectorObserver()
    scheduleConnectorSync()
  },
  { flush: 'post' }
)

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', syncCompactViewport)
  }

  resetConnectorObserver()
  stopMobileFrameTween()
  resetMotion()
})
</script>

<style scoped>
.hardware-callout-scroller {
  width: 100%;
  color: var(--pixso-color-text);
}

.hardware-callout-scroller--viewport-fit {
  width: calc(100vw - var(--hardware-callout-viewport-inset) - var(--hardware-callout-viewport-inset));
  max-width: calc(100vw - var(--hardware-callout-viewport-inset) - var(--hardware-callout-viewport-inset));
  width: min(
    calc(100vw - var(--hardware-callout-viewport-inset) - var(--hardware-callout-viewport-inset)),
    calc(var(--hardware-callout-viewport-height) * var(--hardware-callout-stage-ratio))
  );
}

.hardware-callout-scroller__stage {
  position: relative;
  width: 100%;
  aspect-ratio: var(--hardware-callout-aspect-ratio);
  min-height: 0;
  isolation: isolate;
}

.hardware-callout-scroller__image {
  position: absolute;
  z-index: 1;
  height: auto;
  max-width: none;
  pointer-events: none;
  user-select: none;
}

.hardware-callout-scroller__mobile-play {
  display: none;
}

.hardware-callout-scroller__scroll-hint {
  position: absolute;
  z-index: 2;
  left: 50%;
  top: 98%;
  width: clamp(44px, 4.4vw, 74px);
  height: auto;
  opacity: 0.34;
  filter: drop-shadow(0 0 18px rgba(220, 254, 168, 0.38));
  pointer-events: none;
  transform: translateX(-50%);
  transform-origin: center;
  user-select: none;
  animation: hardware-callout-scroll-hint-breathe 1.65s ease-in-out infinite;
}

.hardware-callout-scroller__scroll-hint--viewport {
  position: fixed;
  z-index: 110;
  left: 50vw;
  top: auto;
  bottom: calc(var(--hardware-callout-scroll-hint-bottom) + env(safe-area-inset-bottom, 0px));
  opacity: 0;
  visibility: hidden;
}

.hardware-callout-scroller__connectors {
  position: absolute;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

.hardware-callout-scroller__connector {
  opacity: 0;
  stroke: var(--hardware-callout-item-color, var(--hardware-callout-accent));
  stroke-width: 2;
  stroke-linecap: round;
}

.hardware-callout-scroller__dot,
.hardware-callout-scroller__note {
  position: absolute;
}

.hardware-callout-scroller__dot {
  z-index: 3;
  width: 23px;
  height: 23px;
  border: 5px solid var(--hardware-callout-item-color, var(--hardware-callout-accent));
  border-radius: 50%;
  background: transparent;
  opacity: 0;
  transform: translate(-50%, -50%) scale(var(--hardware-callout-dot-scale, 0.4));
  transform-origin: center;
  pointer-events: none;
}

.hardware-callout-scroller__note {
  z-index: 4;
  color: var(--pixso-color-text);
  opacity: 0;
  transform: translateY(24px);
}

.hardware-callout-scroller--static .hardware-callout-scroller__connector {
  opacity: 1;
}

.hardware-callout-scroller--static .hardware-callout-scroller__dot {
  opacity: 1;
  --hardware-callout-dot-scale: 1;
}

.hardware-callout-scroller--static .hardware-callout-scroller__note {
  opacity: 1;
  transform: translateY(0);
}

.hardware-callout-scroller__note h3,
.hardware-callout-scroller__note p,
.hardware-callout-scroller__mobile-card h3,
.hardware-callout-scroller__mobile-card p {
  margin: 0;
}

.hardware-callout-scroller__note h3 {
  color: var(--hardware-callout-item-color, var(--hardware-callout-accent));
  font-size: clamp(var(--pixso-fs-28), 3vw, var(--pixso-fs-48));
  line-height: clamp(var(--pixso-lh-28), 3.65vw, var(--pixso-lh-48));
  font-weight: 700;
}

.hardware-callout-scroller__note-title-text {
  display: inline-block;
}

.hardware-callout-scroller__note p {
  font-size: clamp(var(--pixso-fs-14), 1.25vw, var(--pixso-fs-20));
  line-height: clamp(var(--pixso-lh-14), 1.875vw, var(--pixso-lh-20));
  font-weight: 700;
}

.hardware-callout-scroller__note-highlight {
  color: var(--hardware-callout-item-color, var(--hardware-callout-accent));
}

.hardware-callout-scroller__mobile-list {
  display: none;
}

.hardware-callout-scroller--compact.hardware-callout-scroller--viewport-fit {
  width: 100%;
  max-width: 100%;
}

.hardware-callout-scroller--compact .hardware-callout-scroller__stage {
  aspect-ratio: auto;
}

.hardware-callout-scroller--compact .hardware-callout-scroller__image {
  display: block;
  position: relative;
  left: auto !important;
  top: auto !important;
  width: var(--hardware-callout-mobile-image-width) !important;
  height: auto !important;
  margin: 0 auto;
  transform: none !important;
}

.hardware-callout-scroller--compact .hardware-callout-scroller__mobile-play {
  position: absolute;
  z-index: 5;
  left: 50%;
  top: 50%;
  display: flex;
  width: calc(var(--pixso-gap-lg) + var(--pixso-gap-xs) * 2);
  height: calc(var(--pixso-gap-lg) + var(--pixso-gap-xs) * 2);
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.32);
  color: rgba(0, 0, 0, 0.86);
  cursor: pointer;
  transform: translate(-50%, -50%);
  transition: transform 180ms ease, background-color 180ms ease;
  backdrop-filter: blur(6px);
}

.hardware-callout-scroller--compact .hardware-callout-scroller__mobile-play:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.96);
  outline-offset: 3px;
}

.hardware-callout-scroller--compact .hardware-callout-scroller__mobile-play:not(:disabled):active {
  transform: translate(-50%, -50%) scale(0.96);
}

.hardware-callout-scroller__mobile-play-icon {
  width: 0;
  height: 0;
  margin-left: var(--pixso-gap-xs);
  border-top: var(--pixso-gap-sm) solid transparent;
  border-bottom: var(--pixso-gap-sm) solid transparent;
  border-left: calc(var(--pixso-gap-sm) + var(--pixso-gap-xs)) solid currentColor;
}

.hardware-callout-scroller--compact .hardware-callout-scroller__connectors,
.hardware-callout-scroller--compact .hardware-callout-scroller__dot,
.hardware-callout-scroller--compact .hardware-callout-scroller__note,
.hardware-callout-scroller--compact .hardware-callout-scroller__scroll-hint {
  display: none;
}

.hardware-callout-scroller--compact .hardware-callout-scroller__mobile-list {
  margin-top: var(--pixso-gap-md);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--pixso-gap-sm);
}

.hardware-callout-scroller--compact .hardware-callout-scroller__mobile-card {
  min-width: 0;
  padding: var(--pixso-fs-20) var(--pixso-fs-20) calc(var(--pixso-fs-16) + var(--pixso-gap-xs) * 0.5);
  border: 1px solid rgba(220, 254, 168, 0.14);
  border-radius: var(--pixso-radius-md);
  background: linear-gradient(180deg, rgba(29, 30, 37, 0.96) 0%, rgba(20, 21, 27, 0.98) 100%);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.24);
}

.hardware-callout-scroller--compact .hardware-callout-scroller__mobile-card h3 {
  color: var(--hardware-callout-item-color, var(--hardware-callout-accent));
  font-size: var(--pixso-fs-20);
  line-height: var(--pixso-lh-20);
  font-weight: 700;
}

.hardware-callout-scroller--compact .hardware-callout-scroller__mobile-card p {
  font-size: var(--pixso-fs-14);
  line-height: var(--pixso-lh-14);
  font-weight: 700;
}

@media (max-width: 768px) {
  .hardware-callout-scroller--compact .hardware-callout-scroller__mobile-list {
    grid-template-columns: 1fr;
  }

  .hardware-callout-scroller--compact .hardware-callout-scroller__mobile-card {
    padding: calc(var(--pixso-fs-16) + var(--pixso-gap-xs) * 0.5) calc(var(--pixso-fs-16) + var(--pixso-gap-xs) * 0.5) var(--pixso-fs-16);
  }

  .hardware-callout-scroller--compact .hardware-callout-scroller__mobile-card h3 {
    font-size: var(--pixso-fs-20);
    line-height: var(--pixso-lh-20);
  }

  .hardware-callout-scroller--compact .hardware-callout-scroller__mobile-card p {
    font-size: var(--pixso-fs-12);
    line-height: var(--pixso-lh-12);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hardware-callout-scroller__connector,
  .hardware-callout-scroller__dot,
  .hardware-callout-scroller__note {
    transition: none;
  }

  .hardware-callout-scroller__scroll-hint {
    animation: none;
    opacity: 0.58;
  }
}

@keyframes hardware-callout-scroll-hint-breathe {
  0%,
  100% {
    opacity: 0.28;
    transform: translateX(-50%) translateY(0) scale(0.92);
  }

  50% {
    opacity: 0.88;
    transform: translateX(-50%) translateY(8px) scale(1);
  }
}
</style>
